import React, { useState, useEffect } from 'react';
import { ModalDialog, ButtonBar, FlowForm } from 't-lib-ui/structure';
import { TextButton, Input } from 't-lib-ui/widgets';
import './RenameFeedPopup.css';

const MAX_NAME_LENGTH = 30;

interface RenameFeedPopupProps {
  show: boolean;
  originalName: string;
  currentCustomName?: string;
  onClose: () => void;
  onSave: (name: string) => void;
  onReset: () => void;
}

const RenameFeedPopup: React.FC<RenameFeedPopupProps> = ({
  show, originalName, currentCustomName, onClose, onSave, onReset,
}) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (show) setValue(currentCustomName ?? originalName);
  }, [show, currentCustomName, originalName]);

  // Autofocus (and select) the input once the dialog has opened. A short delay is
  // needed because ModalDialog blurs the active element when it opens.
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      const el = document.querySelector('.renameFeedInput') as HTMLInputElement | null;
      el?.focus();
      el?.select();
    }, 20);
    return () => clearTimeout(t);
  }, [show]);

  const trimmed = value.trim();
  const baseline = currentCustomName ?? originalName;
  const canSave = !!trimmed && trimmed != baseline;
  const canReset = trimmed != originalName;

  const handleReset = () => setValue(originalName);
  const handleSave = () => {
    if (!canSave) return;
    if (trimmed == originalName) onReset();
    else onSave(trimmed);
  };

  return (
    <ModalDialog
      show={show}
      title="Rename feed"
      closeButton="close"
      onClose={onClose}
      className="renameFeedPopup"
    >
      <p className="renameFeedNote">This name is only visible to you. Up to {MAX_NAME_LENGTH} characters.</p>
      <FlowForm>
        <Input
          className="renameFeedInput"
          value={value}
          maxLength={MAX_NAME_LENGTH}
          placeholder={originalName}
          onInput={v => setValue(v)}
          onEnter={handleSave}
          layout="singleCol"
        />
        <ButtonBar className="full-margin-top" align="right">
          <TextButton type="minor" onClick={onClose} label="Cancel" />
          {currentCustomName && <TextButton type="minor" focussable="sideway" onClick={handleReset} label="Reset" enabled={canReset ? 'enabled' : 'disabled'} />}
          <TextButton type="major" focussable="sideway" onClick={handleSave} label="Save" enabled={canSave ? 'enabled' : 'disabled'} />
        </ButtonBar>
      </FlowForm>
    </ModalDialog>
  );
};

export default RenameFeedPopup;
